// Client-side preferences store (fn-72.1).
//
// **Purpose**: ONE tiny localStorage-backed store that powers BOTH the
// preview-features flag (R1) and agent favorites (R5). No backend — these
// are per-browser cosmetic conveniences, never a security boundary.
//
// **Why a module-singleton + `useSyncExternalStore` (not `useState`+effect):**
//   - Concurrent-safe (no tearing).
//   - Cross-tab sync for free via the `storage` event, wired in `subscribe`.
//   - No default→real flash on mount (the snapshot reads localStorage
//     synchronously on first render).
//
// **The `Object.is`-stable snapshot is load-bearing.** `getSnapshot` MUST
// return the SAME object reference until the underlying localStorage string
// actually changes — otherwise React's `useSyncExternalStore` sees a "new"
// value every render and infinite-loops (and warns "getSnapshot should be
// cached"). We achieve this by caching both the last-seen raw string AND the
// parsed snapshot, and re-parsing ONLY when the raw string differs.
//
// **Same-tab writes don't fire `storage`.** The `storage` event fires in
// OTHER tabs only, never the writing tab. So a write notifies same-tab
// subscribers directly through a module-level listener set, and other tabs
// through the `storage` event.
//
// **Everything is `typeof window`-guarded + try/catch.** Corrupt / absent /
// wrong-version JSON degrades to defaults; a write that throws (quota,
// privacy mode) is swallowed and the in-memory snapshot still updates so the
// current tab stays consistent. Stored JSON is UNTRUSTED: we validate shape
// on read and never `as`-cast it.

/** The namespaced + versioned envelope key. Bump the suffix on a breaking
 *  shape change so an old envelope reads as wrong-version → defaults. */
export const PREFS_STORAGE_KEY = 'sb-prefs:v1';

/** Current envelope schema version. A stored `version` mismatch → defaults. */
export const PREFS_VERSION = 1;

/**
 * The public, immutable snapshot React renders from. `favorites` is a
 * `ReadonlySet` (the on-disk array is rehydrated to a Set here so membership
 * checks are O(1) and the consumer can't mutate the cached reference).
 */
export interface PrefsSnapshot {
  readonly previewFeatures: boolean;
  readonly favorites: ReadonlySet<string>;
}

/** The hard-coded defaults: preview OFF, no favorites. */
function makeDefaults(): PrefsSnapshot {
  return { previewFeatures: false, favorites: new Set<string>() };
}

/** A stable defaults reference for the server snapshot (SSR / no-window). */
const SERVER_SNAPSHOT: PrefsSnapshot = makeDefaults();

// ── Cache for the `Object.is`-stable client snapshot ───────────────────────
// `cachedRaw` is the last localStorage string we parsed; `cachedSnapshot` is
// its parsed form. `cachedRaw === undefined` means "not yet read" (distinct
// from a stored `null`, which means "key absent"). We re-parse ONLY when the
// raw string changed.
let cachedRaw: string | null | undefined;
let cachedSnapshot: PrefsSnapshot = makeDefaults();

// In-memory fallback for when persistence is impossible (no `localStorage`,
// access denied, or a `setItem` that threw on quota / privacy mode). When set,
// `readRaw` returns THIS instead of storage so the writing tab stays
// internally consistent for the session even though nothing was persisted.
// Cleared on a successful persist so real (incl. cross-tab) storage wins again.
let memoryRaw: string | null = null;
let memoryFallbackActive = false;

/** Same-tab subscribers. `storage` doesn't fire in the writing tab, so a
 *  write calls these directly. */
const listeners = new Set<() => void>();

/**
 * Resolve the `Storage` object, or `null` when unavailable. The
 * `window.localStorage` PROPERTY access can itself throw a `SecurityError`
 * (cookies/storage disabled, sandboxed iframe) — so the property read is
 * inside the try/catch, not just the later `getItem`/`setItem` calls. NEVER
 * throws.
 */
function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Read the effective raw envelope string. Honors the in-memory fallback when
 * persistence failed; otherwise reads `localStorage`. Returns `null` on
 * absence / any access error. NEVER throws.
 */
function readRaw(): string | null {
  if (memoryFallbackActive) return memoryRaw;
  const storage = getStorage();
  if (storage === null) return null;
  try {
    return storage.getItem(PREFS_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Parse an untrusted raw envelope into a snapshot. Any shape deviation,
 * version mismatch, parse error, or absent string → defaults. NEVER throws.
 * Non-string favorite entries are ignored (we trust nothing in the JSON).
 */
function parseRaw(raw: string | null): PrefsSnapshot {
  if (raw === null) return makeDefaults();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return makeDefaults();
  }
  if (typeof parsed !== 'object' || parsed === null) return makeDefaults();
  const envelope = parsed as Record<string, unknown>;
  if (envelope.version !== PREFS_VERSION) return makeDefaults();

  const previewFeatures = envelope.previewFeatures === true;

  const favorites = new Set<string>();
  if (Array.isArray(envelope.favorites)) {
    for (const entry of envelope.favorites) {
      if (typeof entry === 'string') favorites.add(entry);
    }
  }
  return { previewFeatures, favorites };
}

/**
 * `getSnapshot` — returns an `Object.is`-stable snapshot. Re-parses ONLY when
 * the raw localStorage string changed since the last call; otherwise returns
 * the cached reference so React doesn't see a "new" value every render.
 */
export function getSnapshot(): PrefsSnapshot {
  const raw = readRaw();
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  cachedSnapshot = parseRaw(raw);
  return cachedSnapshot;
}

/** `getServerSnapshot` — defaults, no window access. Stable reference. */
export function getServerSnapshot(): PrefsSnapshot {
  return SERVER_SNAPSHOT;
}

/**
 * `subscribe` — wires BOTH the cross-tab `window` `storage` event AND a
 * same-tab listener (the writing tab never receives `storage`). The storage
 * handler is filtered to OUR key (and a full-clear, where `key === null`).
 * Returns an unsubscribe that removes both.
 */
export function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);

  const onStorage = (event: StorageEvent): void => {
    // `event.key === null` is a `localStorage.clear()` (affects every key).
    if (event.key === PREFS_STORAGE_KEY || event.key === null) onChange();
  };
  // The `storage` event is dispatched on `window`, independent of whether
  // `localStorage` itself is accessible; guard only on `window` existing.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }

  return () => {
    listeners.delete(onChange);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}

/** Notify same-tab subscribers after a local write. */
function notifyLocal(): void {
  for (const listener of listeners) listener();
}

/**
 * Persist a snapshot and notify same-tab subscribers. The write is
 * try/catch-guarded (quota / privacy mode / storage denied). On a persist
 * FAILURE we flip on the in-memory fallback so the current tab still reflects
 * the change this session — without it, the next `getSnapshot` would re-read
 * the stale storage string, see a mismatch, and silently revert. On a persist
 * SUCCESS we clear the fallback so real (incl. cross-tab) storage wins again.
 * `favorites` is serialized as a sorted string[] (sorted only for stable,
 * diff-friendly storage — order is not semantic).
 */
function write(next: PrefsSnapshot): void {
  const serialized = JSON.stringify({
    version: PREFS_VERSION,
    previewFeatures: next.previewFeatures,
    favorites: [...next.favorites].sort(),
  });

  let persisted = false;
  const storage = getStorage();
  if (storage !== null) {
    try {
      storage.setItem(PREFS_STORAGE_KEY, serialized);
      persisted = true;
    } catch {
      // Quota / privacy mode: fall through to the in-memory fallback below.
    }
  }

  if (persisted) {
    // Real storage now holds `serialized`; the fallback is no longer needed.
    memoryFallbackActive = false;
    memoryRaw = null;
  } else {
    // Couldn't persist — keep the value in memory so `readRaw` returns it and
    // the snapshot doesn't revert on the next render.
    memoryFallbackActive = true;
    memoryRaw = serialized;
  }

  // Seed the cache from what we just wrote so the next `getSnapshot` returns
  // this exact reference (stable identity) without a re-parse round-trip.
  cachedRaw = serialized;
  cachedSnapshot = next;
  notifyLocal();
}

// ── Mutators (called by the hooks) ─────────────────────────────────────────

/** Set the preview-features flag to an explicit value. No-op if unchanged. */
export function setPreviewFeatures(enabled: boolean): void {
  const current = getSnapshot();
  if (current.previewFeatures === enabled) return;
  write({ previewFeatures: enabled, favorites: current.favorites });
}

/** Flip the preview-features flag. */
export function togglePreviewFeatures(): void {
  const current = getSnapshot();
  write({ previewFeatures: !current.previewFeatures, favorites: current.favorites });
}

/** Add or remove `key` from favorites. No-op if already in the desired state. */
export function setFavorite(key: string, favorite: boolean): void {
  const current = getSnapshot();
  const has = current.favorites.has(key);
  if (has === favorite) return;
  const favorites = new Set(current.favorites);
  if (favorite) favorites.add(key);
  else favorites.delete(key);
  write({ previewFeatures: current.previewFeatures, favorites });
}

/** Toggle `key`'s favorite membership. */
export function toggleFavorite(key: string): void {
  const current = getSnapshot();
  setFavorite(key, !current.favorites.has(key));
}

/**
 * TEST-ONLY: reset the module-level snapshot cache so a test starts from a
 * clean read. Vitest `isolate: true` resets the module registry between test
 * FILES, but within a file (or after directly poking localStorage to simulate
 * another tab's write) the cache must be cleared to force a re-parse on the
 * next `getSnapshot`. Deliberately does NOT touch the `listeners` set — those
 * are live subscriptions a test owns and tears down itself via the
 * `subscribe` return value. Not part of the public prefs API.
 */
export function __resetPrefsCacheForTests(): void {
  cachedRaw = undefined;
  cachedSnapshot = makeDefaults();
  memoryFallbackActive = false;
  memoryRaw = null;
}
